/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Brian Bal
 */

import React, { useState, useEffect, useRef } from "react"
import * as localForage from "localforage"
import { useLocation } from "react-router"
import { useHistory } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { ProtectedRoute } from "../../components/ProtectedRoute"
import { WrappedCreateMomentPage } from "../WrappedCreateMomentPage"
import { WrappedEditMomentPage } from "../WrappedEditMomentPage"
import { WrappedConvertChurnedDialog } from "../WrappedConvertChurnedDialog"
import {
    trackEvent,
    isSmallScreen,
    isSmallMobileScreen,
    sharedMomentToCZ,
    getProperty,
    VideoControlButton,
    Icon,
    Color,
    Row,
    Route,
    Element,
    MaxLoopOverlay,
    useHotKeyHandler,
    MulticamOnboardingDialog,
} from "trux"
import {
    VideoContainer,
    usePlaylistControls,
    useVideoContainerRef,
    useMetaVideoInfo,
    useVideoControls,
    getCameras,
    getAllSpotlights,
} from "trux/dist/organisms/VideoPlayer"
import { MomentTitle } from "trux/dist/organisms/MomentTitle"
import Feedback from "../Feedback"
import * as links from "../../util/links"
import { videoPlayerBasePath } from "../../util/apiFolder"
import { nativeShareMoment } from "../ShareDialog"
import { SaveButton } from "../../components/SaveButton"
import { Page } from "../Navigation"
import {
    useGetMomentCollectionInfoQuery,
    useContentAccessForTeamLazyQuery,
} from "../../generated/graphql"

import { getPageViews } from "../../util/analytics"
import "./GamePage.css"
import { PersonalizeMomentPage } from "../PersonalizeMomentPage"
import { startBenchmark } from "trux"
import { WrappedSaveMomentFlow } from "../WrappedSaveMomentFlow"

// Helpers

const AddtlRoutes = ({
    token,
    editEnabled,
    createEnabled,
    containerRef,
    saveButtonRef,
}) => {
    return (
        <>
            <ProtectedRoute
                path="/:team_name/games/:game_id/:playlist_id/:event_number/create-moment"
                component={WrappedCreateMomentPage}
                isLoggedIn={token?.user_id}
                hasPermissions={createEnabled}
                permissionsLoaded={createEnabled !== null}
                videoContainerRef={containerRef}
                dismissLogin={(history) =>
                    history.push(
                        history.location.pathname.replace("/create-moment", "")
                    )
                }
            />
            <ProtectedRoute
                path="/:team_name/games/:game_id/:playlist_id/:event_number/edit-moment"
                component={WrappedEditMomentPage}
                isLoggedIn={token?.user_id}
                hasPermissions={createEnabled && editEnabled}
                permissionsLoaded={
                    createEnabled !== null && editEnabled !== null
                }
                videoContainerRef={containerRef}
                dismissLogin={(history) =>
                    history.push(
                        history.location.pathname.replace("/edit-moment", "")
                    )
                }
            />
            <Route
                path="/:team_name/games/:game_id/:playlist_id/:event_number/personalize"
                render={(props) => {
                    return (
                        <PersonalizeMomentPage
                            {...props}
                            videoContainerRef={containerRef}
                        />
                    )
                }}
            />
            <ProtectedRoute
                path="/:team_name/games/:game_id/:playlist_id/:event_number/save-moment"
                component={WrappedSaveMomentFlow}
                saveButtonRef={saveButtonRef}
                token={token}
                isLoggedIn={token?.user_id}
                hasPermissions={token?.user_id}
                permissionsLoaded={token !== null}
                dismissLogin={(history) => {
                    history.push(
                        history.location.pathname
                            .replace("/save-moment", "")
                            .replace("start=", "time/")
                    )
                }}
            />
        </>
    )
}

const MemoizedAddtlRoutes = React.memo(AddtlRoutes)

// Game Page

export const GamePage = (props) => {
    //// Collect Data
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    const playlistId = props.match.params.playlist_id
    const gameId = props.match.params.game_id
    const fullEventNumber = props.match.params.event_number
    const teamName = props.match.params.team_name
    const extras = links.getUrlExtras(window.location.hash)
    const [color, setColor] = useState(
        isSmallMobileScreen() ? Color.Trace : Color.White2
    )
    const eventNumberParts = fullEventNumber.split(":")
    const eventNumber = eventNumberParts[0]
    let urlCamera = "tracecam"
    if (eventNumberParts.length > 1) {
        urlCamera = eventNumberParts[1]
    }

    const containerRef = useVideoContainerRef()
    const {
        currentMoment,
        goToNext,
        goToPrev,
        goToItem,
        onNext,
        onPrev,
        setItems,
        items: playlistItems,
    } = usePlaylistControls(containerRef)

    const SaveButtonRef = useRef()

    const [maxLoopCount, setMaxLoopCount] = useState(7)
    const clipViewsLoopDebounce = useRef(0)
    const [clipViews, setClipViews] = useState(-1)
    const [startAt, setStartAt] = useState(0)
    const { paused, loopCount, multicamOpen } = useMetaVideoInfo(containerRef)

    const [showMulticamOnboarding, setShowMulticamOnboarding] = useState(false)
    const [showMulticamTooltip, setShowMulticamTooltip] = useState(false)
    const [showChurnedDialog, setShowChurnedDialog] = useState(false)
    const [respondToHotkeys, setRespondToHotkeys] = useState(true)
    const [forceActive, setForceActive] = useState(false)

    const via = useSelector((state) => {
        return state.via
    })

    const game = useSelector((state) => {
        return state.game.value
    })

    const video = useSelector((state) => {
        return state.video.value
    })

    const team = useSelector((state) => {
        return state.team.value
    })

    const user = useSelector((state) => {
        return state.user.value
    })

    const playlist = useSelector((state) => {
        return state.video.value.playlist
    })

    const event = useSelector((state) => {
        return state.video.value.event
    })

    const currentSource = useSelector((state) => {
        return state.video.value.source
    })

    // camera chosen in events panel filter
    const cameraFilter = useSelector((state) => {
        return state.selectedCamera
    })
    const cameraFilterRef = useRef(cameraFilter)

    // camera that the next moment should default to
    const videoPlayerDefaultCamera = useSelector((state) => {
        return urlCamera
    })

    const activeCamera = useSelector((state) => {
        return state.video.value.camera
    })

    const multicamOnboardingKey = game
        ? `multicam_onboarding_shown_${game.game_id}`
        : null
    const multicamTooktipKey = game
        ? `multicam_tooltip_shown_${game.game_id}`
        : null

    const { acl, enableAddTags } = useSelector((state) => {
        let acl = state.acl.value
        let createEnabled = state.acl.create_moment_tool
        let editEnabled =
            getProperty("video.value.event.meta.type", state, "touch") !==
            "FullGameVideo"
        let enableAddTags = createEnabled && editEnabled
        return {
            acl,
            createEnabled,
            editEnabled,
            enableAddTags,
        }
    })

    const playlistEvents = useSelector((state) => {
        return state.video.value.playlistEvents
    })
    const playlistMoments = eventsToMoments(playlistEvents, game, playlist)
    const cameras = getCameras(game ? game.events : [])
    // we always need at least one camera
    if (cameras.length === 0) {
        cameras.push("tracecam")
    }

    const processing = getProperty("processing", game, false)

    let subtitle = "--"
    if (playlist) {
        if (playlistId === "full-game") {
            subtitle = event.title
        } else {
            subtitle = playlist.title
        }
    }

    // redirects family games to the chooser if they land on a tactical playlist
    useEffect(() => {
        const isFamily = team ? team.division?.type === "family" : false
        let disabledFamilyOptions = [
            // "goals", // allowed for now (ChoosePlaylist/index.js#L30)
            "progression",
            "def-breakdown",
            "touch-chains",
            "attacking",
            "middle",
            "defensive",
        ]

        if (disabledFamilyOptions.includes(playlistId) && isFamily) {
            history.replace(
                `/${teamName}/games/${gameId}/full-game/first/choose`
            )
        }
    }, [playlistId, team])

    // initialize or increment the loop count when event changes
    useEffect(() => {
        if (event) {
            if (clipViews >= 0) {
                // if moment views are loaded we can just increment the views
                setClipViews(clipViews + 1)
            } else {
                // Load current moment views, if not already loaded
                getPageViews(game.events.map((e) => e.game_clip_id))
                    .then((count) => {
                        setClipViews(count)
                    })
                    .catch((e) => {
                        console.warn(e)
                    })
            }
        }
    }, [event])

    // Increment clipViews when loop count changes
    useEffect(() => {
        if (event) {
            if (loopCount > 0 && clipViewsLoopDebounce.current !== loopCount) {
                // if moment views are loaded we can just increment the views
                clipViewsLoopDebounce.current = loopCount
                setClipViews(clipViews + 1)
            }
        }
    }, [loopCount])

    // make sure url event and playlist controller match
    useEffect(() => {
        if (event) {
            let momentEventNumber = getProperty(
                "event_number",
                currentMoment,
                null
            )
            // only force playlist controller to change when needed
            // eslint-disable-next-line eqeqeq
            if (momentEventNumber && eventNumber != momentEventNumber) {
                goToItem(event.game_clip_id)
            }
        }
    }, [event, eventNumber, currentMoment, goToItem])

    //// Handlers

    const onShareMoment = (e) => {
        const isMulticam = cameras && cameras.length > 1
        if (isMulticam) {
            let camera = activeCamera
                ? activeCamera.charAt(0).toUpperCase() + activeCamera.slice(1)
                : "Multicam"
            if (camera.includes("Phone")) {
                camera = camera.split("-")[0]
            }
            const eventAction = `Share ${camera} Moment`
            trackEvent({
                action: eventAction,
                label: "Share Moment",
                element: e ? e.target : null,
            })
        }
        const isFamily = team ? team.division?.type === "family" : false
        if (isFamily) {
            const eventAction = "Share Family Moment"
            trackEvent({
                action: eventAction,
                label: "Share Moment",
                element: e ? e.target : null,
            })
        }
        let ok = nativeShareMoment(game, playlist, event)
        if (ok) {
            trackEvent({
                action: "Native Share Pressed",
                label: "Share Button",
                element: document.querySelector(".VideoControls *"),
            })
            sharedMomentToCZ({
                type: "native share",
                user_id: user?.user_id,
                where: "main",
                moment_id: event?.game_clip_id,
            })
            e.preventDefault()
        } else {
            props.history.push(
                links.gameShareUrl(
                    teamName,
                    playlistId,
                    gameId,
                    fullEventNumber
                )
            )
        }
    }

    useEffect(() => {
        document.addEventListener("trc-video-on-ended", goToNext)
        return () => {
            document.removeEventListener("trc-video-on-ended", goToNext)
        }
    }, [goToNext])

    const shouldShowMulticamOnboarding = async (game) => {
        const alreadyShown = await localForage.getItem(multicamOnboardingKey)
        const shouldShow = alreadyShown ? false : true
        return shouldShow
    }

    const shouldShowMulticamTooltip = async (game) => {
        const alreadyShown = await localForage.getItem(multicamTooktipKey)
        const shouldShow = alreadyShown ? false : true
        return shouldShow
    }

    useEffect(() => {
        // Each time the game changes, we want to determine whether or not
        // to show the multicam onboarding dialog.
        const isMulticam = cameras && cameras.length > 1
        if (game && isMulticam && multicamOnboardingKey) {
            shouldShowMulticamOnboarding(game).then((shouldShow) => {
                if (shouldShow) {
                    setShowMulticamOnboarding(shouldShow)
                    localForage.setItem(multicamOnboardingKey, true)
                }
            })
        }
    }, [game])

    useEffect(() => {
        // Each time the event/source changes, we want to determine whether or not
        // to show the multicam tooltip.
        const shouldShow = event && game
        if (shouldShow) {
            const isGoalOrHit = event.tags?.some(
                (el) =>
                    el.name === "goal" ||
                    el.name === "Goal" ||
                    el.name === "hit"
            )
            const isMulticamGoal = event.sources && event.sources.length > 0
            if (isGoalOrHit && isMulticamGoal && multicamTooktipKey) {
                shouldShowMulticamTooltip(game).then((shouldShow) => {
                    if (shouldShow) {
                        setShowMulticamTooltip(shouldShow)
                        localForage.setItem(multicamTooktipKey, true)
                    }
                })
            }
        }
    }, [event, game])

    useEffect(() => {
        onNext((moment) => {
            if (showMulticamTooltip) setShowMulticamTooltip(false)
            let urlParts = window.location.hash.split("/")
            let postFix = ""
            for (let i = 0; i < urlParts.length; i++) {
                if (i > 5) {
                    postFix += "/" + urlParts[i]
                }
            }
            let c =
                cameraFilter === "all" || cameraFilter === "tracecam"
                    ? ""
                    : `:${cameraFilter}`
            startBenchmark("PlayNext")
            console.log(
                "onNext",
                `/${teamName}/games/${gameId}/${playlistId}/${moment.event_number}${c}${postFix}`
            )
            props.history.push(
                `/${teamName}/games/${gameId}/${playlistId}/${moment.event_number}${c}${postFix}`
            )
        })

        onPrev((moment) => {
            let urlParts = window.location.hash.split("/")
            let postFix = ""
            for (let i = 0; i < urlParts.length; i++) {
                if (i > 5) {
                    postFix += "/" + urlParts[i]
                }
            }
            let c =
                cameraFilter === "all" || cameraFilter === "tracecam"
                    ? ""
                    : `:${cameraFilter}`
            console.log(
                "onPrev",
                `/${teamName}/games/${gameId}/${playlistId}/${moment.event_number}${c}${postFix}`
            )
            props.history.push(
                `/${teamName}/games/${gameId}/${playlistId}/${moment.event_number}${c}${postFix}`
            )
        })
    }, [
        onNext,
        onPrev,
        props.history,
        teamName,
        gameId,
        playlistId,
        cameraFilter,
    ])

    const toast = (msg) => {
        dispatch({
            type: "COMMON_SET",
            key: "toast",
            value: msg,
        })
    }

    const handleResize = () => {
        setColor(isSmallMobileScreen() ? Color.Trace : Color.White2)
    }

    const handleAfterOnClickTraceIdButton = (e) => {
        // If this is a family moment, track it
        const isFamily = team?.division?.type === "family"
        if (isFamily) {
            const eventAction = "Share Family Moment"
            trackEvent({
                action: eventAction,
                label: "Share Moment",
                element: e ? e.target : null,
            })
        }
    }

    const maxLoopPlayPressed = () => {
        setMaxLoopCount(maxLoopCount * 3)
    }

    const openMomentsSelector = () => {
        console.log(
            "openMomentsSelector",
            links.gameNotesUrl(teamName, playlistId, gameId, fullEventNumber)
        )
        props.history.push(
            links.gameNotesUrl(teamName, playlistId, gameId, fullEventNumber)
        )
    }

    const gotoPlayerPage = (player) => {
        if (player) {
            console.log(
                "gotoPlayerPage",
                links.gamePlayerDetailsUrl(
                    teamName,
                    playlistId,
                    gameId,
                    fullEventNumber,
                    player.trace_number
                )
            )
            props.history.push(
                links.gamePlayerDetailsUrl(
                    teamName,
                    playlistId,
                    gameId,
                    fullEventNumber,
                    player.trace_number
                )
            )
        }
    }

    useEffect(() => {
        if (showMulticamTooltip) {
            setShowMulticamTooltip(false)
        }
    }, [multicamOpen])

    useEffect(() => {
        if (JSON.stringify(playlistItems) !== JSON.stringify(playlistMoments)) {
            let en
            try {
                en = eventNumber.startsWith("C")
                    ? parseInt(eventNumber.substring(1))
                    : parseInt(eventNumber, 10)
                if (isNaN(en)) {
                    en = 10
                }
            } catch (e) {
                en = null
            }
            setItems(playlistMoments, en)
        }
    }, [playlistId, playlistMoments, setItems])

    useEffect(() => {
        // Grab the start time from the URL if we need to & have it
        let params = new URLSearchParams(location.search)
        let startTime = parseInt(params?.get("start_at"))
        if (startTime) {
            setStartAt(startTime)
        }
        // Determine whether we should force the active state
        const forceActiveVideoPlayerState =
            location.pathname.includes("create-moment") ||
            location.pathname.includes("save-moment")
        if (forceActive !== forceActiveVideoPlayerState)
            setForceActive(forceActiveVideoPlayerState)
    }, [location])

    useEffect(() => {
        document.body.className += " game"
        window.addEventListener("resize", handleResize)
        return () => {
            document.body.className = document.body.className.replace(
                " game",
                ""
            )
            window.removeEventListener("resize", handleResize)
        }
    }, [])

    // Video controls
    const { play, pause, selectCamera } = useVideoControls(containerRef)

    useHotKeyHandler([" "], () => {
        if (!respondToHotkeys) return
        if (paused) {
            play()
            toast("Play")
        } else {
            pause()
            toast("Pause")
        }
    })
    useHotKeyHandler(["ArrowLeft"], () => {
        if (!respondToHotkeys) return
        goToPrev()
        toast("Previous")
    })
    useHotKeyHandler(["ArrowRight"], () => {
        if (!respondToHotkeys) return
        goToNext()
        toast("Next")
    })

    useEffect(() => {
        const onSourceChange = (e) => {
            let cam =
                e.detail === "all" || e.detail === "tracecam"
                    ? ""
                    : `:${e.detail}`
            let path = props.history.location.pathname
            let parts = path.split("/")
            if (parts.length > 5) {
                if (cam) {
                    parts[5] = `${eventNumber}${cam}`
                } else {
                    parts[5] = `${eventNumber}`
                }
                let path = parts.join("/")
                if (props.history.location.pathname !== path) {
                    console.log("onSourceChange", path)
                    props.history.push(path)
                }
            }
        }
        document.addEventListener("trc-source-changed", onSourceChange)
        return () => {
            document.removeEventListener("trc-source-changed", onSourceChange)
        }
    }, [eventNumber, cameraFilter])

    useEffect(() => {
        let qa = window.location.hash.includes("/qa/")
        let effectiveCameraFilter = cameraFilter === "all" ? "" : cameraFilter
        if (
            !qa &&
            effectiveCameraFilter !== urlCamera &&
            cameraFilter !== cameraFilterRef.current
        ) {
            cameraFilterRef.current = cameraFilter
            let cam =
                cameraFilter === "all" || cameraFilter === "tracecam"
                    ? ""
                    : `:${cameraFilter}`
            let path = props.history.location.pathname
            let parts = path.split("/")
            if (parts.length > 5) {
                if (cam) {
                    parts[5] = `${eventNumber}${cam}`
                } else {
                    parts[5] = `${eventNumber}`
                }
                let path = parts.join("/")
                if (props.history.location.pathname !== path) {
                    console.log("useEffect camera change", path)
                    props.history.push(path)
                }
            }
        } else if (!qa && effectiveCameraFilter !== cameraFilter) {
            console.log("forcing url camera", urlCamera)
            selectCamera(urlCamera)
        }
    }, [cameraFilter, cameraFilterRef])

    const onRadarEvent = (e) => {
        if (e.type === "tracer-click") {
            gotoPlayerPage(e)
        }
    }

    const getPlayerForPersonalPlaylist = () => {
        let result = null
        const tracerId = playlistId?.includes("highlight")
            ? playlistId.split("-")[2]
            : null
        // Iterate the home teams players to get the player that matches the userId
        if (tracerId && game?.home_team?.players) {
            result = game.home_team.players.find(
                (player) => parseInt(player.id) === parseInt(tracerId)
            )
        }
        // Check that the player is a person
        result =
            result && !result.user.first_name.startsWith("Tracer")
                ? result
                : null
        return result
    }

    const { data: momentCollectionData } = useGetMomentCollectionInfoQuery({
        variables: {
            token: props.token,
            game_clip_id: event?.game_clip_id,
            user_id: props.token?.user_id,
        },
        skip: !props.token !== null || !event?.game_clip_id,
    })

    const activePlayer = getPlayerForPersonalPlaylist()
    const activePlayerId = activePlayer ? activePlayer.user.user_id : null
    const startAtOnceTime =
        currentMoment && extras.time
            ? extras.time - currentMoment.full_game_time
            : null

    // Let's replace the event with the currentSource if we need to
    let currentEvent = event ? { ...event } : null
    if (currentSource && event) {
        let matchingEvent = event.sources?.find((source) => {
            return (
                parseInt(source.game_clip_id) ===
                parseInt(currentSource.game_clip_id)
            )
        })
        if (matchingEvent) {
            currentEvent = { ...matchingEvent }
            currentEvent.full_game_time = event.full_game_time
        }
    }
    // If we're viewing a different source than default, let's copy that source's tags
    // onto the moment b/c MomentRow uses the tags on the moment itself to generate
    // tags rather than the tags prop
    const tags = currentEvent ? currentEvent.tags : event ? event.tags : []
    if (currentEvent !== event) {
        currentEvent.tags = tags
        currentEvent.display_number = event.display_number
    }

    // Monitor severable variables, including "url"/location changes, and determine
    // whether we're on a route in which the hotkeys should be disabled
    // Monitor "url"/location changes and determine whether we're on a route
    // in which the hotkeys should be disabled
    useEffect(() => {
        const disableHotkeysBasedOnDialogs =
            showMulticamOnboarding || showMulticamTooltip || showChurnedDialog
        const disableHotkeysBasedOnLocation =
            location?.pathname &&
            (location.pathname.includes("edit-moment") ||
                location.pathname.includes("create-moment") ||
                location.pathname.includes("personalize") ||
                location.pathname.includes("save-moment"))
        if (disableHotkeysBasedOnDialogs || disableHotkeysBasedOnLocation) {
            setRespondToHotkeys(false)
        } else if (
            !disableHotkeysBasedOnLocation &&
            !disableHotkeysBasedOnLocation
        ) {
            setRespondToHotkeys(true)
        }
    }, [
        showMulticamOnboarding,
        showMulticamTooltip,
        showChurnedDialog,
        location,
    ])

    const [fetchContentAccess] = useContentAccessForTeamLazyQuery({})

    useEffect(() => {
        // Get the content access
        if (team && props.token) {
            fetchContentAccess({
                variables: {
                    team_id: team?.team_id,
                    game_id: game?.game_id,
                    token: props.token,
                },
                skip: !props.token || !team || !game,
            }).then((contentAccess) => {
                const access = contentAccess?.data?.contentAccessForTeam
                const allowed = access ? access.allowed : true
                const shouldShow =
                    !allowed && !window.location.href?.includes("success")
                setShowChurnedDialog(shouldShow)
            })
        }
    }, [user, props.token, team])

    return (
        <Page isGameResults noPadding>
            <MemoizedAddtlRoutes
                token={props.token}
                createEnabled={props.createEnabled}
                editEnabled={props.editEnabled}
                containerRef={containerRef}
                saveButtonRef={SaveButtonRef}
            />
            <div className="Game">
                {showChurnedDialog && (
                    <WrappedConvertChurnedDialog
                        videoContainerRef={containerRef}
                        hideCloseButton={true}
                    />
                )}
                <div className="GameVideoSection">
                    <VideoContainer
                        forceActive={forceActive}
                        noMulticam={false}
                        startAt={startAt}
                        containerRef={containerRef}
                        cameras={cameras}
                        defaultCamera={videoPlayerDefaultCamera}
                        items={playlistMoments}
                        radar
                        spotlight
                        onRadarEvent={onRadarEvent}
                        toaster={toast}
                        startAtOnce={startAtOnceTime}
                        coachAudio={playlistId === "coach-notes"}
                        trackingUserId={user?.user_id}
                        trackingCategory="GO > Video > Play"
                        trackingAction="Game Page"
                        trackingPlayerType="Standard"
                        fullScreenElementSelector={".VideoPlayer"}
                        multicamTooltipOpen={
                            showMulticamTooltip && !showChurnedDialog
                        }
                        onMulticamTooltipClose={() =>
                            setShowMulticamTooltip(false)
                        }
                        extraVideoControls={
                            <>
                                <VideoControlButton
                                    trkAction="Share Pressed"
                                    icon={Icon.Type.Action}
                                    onClick={onShareMoment}
                                    iconSize="24"
                                    title="Share"
                                />
                                <div
                                    className="SaveButtonContainer mobile"
                                    ref={SaveButtonRef}
                                >
                                    <SaveButton
                                        game={game}
                                        video={video}
                                        history={history}
                                        afterOnClick={null}
                                        location={location}
                                        saveUrl={"save-moment/"}
                                        createUrl={"create-moment"}
                                        trkAction={"SaveButton Pressed"}
                                    />
                                </div>
                            </>
                        }
                        extraUserControls={
                            <>
                                <VideoControlButton
                                    trkAction="Players Pressed"
                                    icon={Icon.Type.PlayersGeneric}
                                    iconColor={color}
                                    onClick={(e) =>
                                        props.history.push(
                                            links.gamePlayersUrl(
                                                teamName,
                                                playlistId,
                                                gameId,
                                                fullEventNumber
                                            )
                                        )
                                    }
                                    iconSize="24"
                                    hideSM
                                    title="Players"
                                />
                                <VideoControlButton
                                    marginRight={8}
                                    trkAction="More Pressed"
                                    icon={Icon.Type.More}
                                    iconColor={color}
                                    onClick={(e) =>
                                        props.history.push(
                                            links.gameOptionsUrl(
                                                teamName,
                                                playlistId,
                                                gameId,
                                                fullEventNumber
                                            )
                                        )
                                    }
                                    iconSize="24"
                                    title="Moment options"
                                />
                                <div
                                    className="SaveButtonContainer desktop"
                                    ref={SaveButtonRef}
                                >
                                    <SaveButton
                                        game={game}
                                        video={video}
                                        history={history}
                                        afterOnClick={null}
                                        location={location}
                                        saveUrl={"save-moment/"}
                                        createUrl={"create-moment"}
                                        trkAction={"SaveButton Pressed"}
                                    />
                                </div>
                            </>
                        }
                    >
                        <Row className="clickable-controls">
                            <MomentTitle
                                playlist={subtitle}
                                moment={currentEvent}
                                momentCount={playlistEvents.length}
                                highlightPlayer={getProperty(
                                    "scope.player",
                                    playlist,
                                    null
                                )}
                                tags={getProperty("tags", currentEvent, [])}
                                players={getProperty("players", event, [])}
                                onAvatarClick={gotoPlayerPage}
                                homeColor={getProperty(
                                    "home_team.jersey_color",
                                    game,
                                    "#000000"
                                )}
                                awayColor={getProperty(
                                    "away_team.jersey_color",
                                    game,
                                    "#ffffff"
                                )}
                                clipViews={clipViews > 0 ? clipViews : 1}
                                showAddTagsButton={enableAddTags}
                                onCheveronClick={openMomentsSelector}
                                avatarSize={isSmallScreen() ? 32 : 40}
                            />
                            <Element className="clickable-area" />
                        </Row>
                    </VideoContainer>

                    <Feedback
                        videoContainerRef={containerRef}
                        extras={extras}
                        toast={toast}
                        game={game}
                        event={event}
                        user={user}
                    />

                    <MaxLoopOverlay
                        event={event}
                        videoContainerRef={containerRef}
                        maxLoopCount={maxLoopCount}
                        loopCount={loopCount}
                        showOverlay={false}
                        playPressed={maxLoopPlayPressed}
                        sharePressed={onShareMoment}
                        acl={acl}
                        saveUrl={links
                            .gameShareUrl(
                                teamName,
                                playlistId,
                                gameId,
                                fullEventNumber
                            )
                            .replace("share", "save/profile")}
                        momentCollectionData={momentCollectionData}
                    />
                    {showMulticamOnboarding && !showChurnedDialog && (
                        <MulticamOnboardingDialog
                            open={showMulticamOnboarding}
                            onClose={(e) => setShowMulticamOnboarding(false)}
                        />
                    )}
                </div>
            </div>
        </Page>
    )
}

function eventsToMoments(events, game, playlist) {
    let moments = []
    let focusPlayer = getProperty("scope.player.trace_number", playlist, "")

    let players = {}
    for (let p of getProperty("home_team.players", game, [])) {
        players[p.trace_number] = {
            id: getProperty("trace_number", p, ""),
            user_id: getProperty("user.user_id", p, ""),
            first_name: getProperty("user.first_name", p, ""),
            last_name: getProperty("user.last_name", p, ""),
            abr: getProperty("user.abbr", p, ""),
            jersey_number: getProperty("jersey", p, ""),
            pos: "",
            team: getProperty("home_team.name", game, ""),
            name: getProperty("user.abbr", p, ""),
        }
    }

    let timeline = getProperty("timeline", game, { spotlight: [] })
    for (let event of events) {
        let m = eventToMoment(event, game, focusPlayer, players)
        let spots = getAllSpotlights(event, playlist, timeline)
        if (spots) {
            m.spotlights = spots
        }
        moments.push(m)
    }

    return moments
}

function eventToMoment(event, game, focusPlayer, players) {
    let eventSources = getProperty("sources", event, [])
    let sources = []
    for (let es of eventSources) {
        sources.push(eventToMoment(es, game, focusPlayer, players))
    }

    let radar = {
        focus_players: getProperty("meta.all_trace_numbers", event, []),
        focus_player: focusPlayer,
    }
    radar.players = players

    let meta = { ...event.meta }
    if (!meta.camera) {
        meta.camera = {
            category: "camera",
            name: "tracecam",
            title: "TraceCam",
            type: "Trace",
        }
    }

    return {
        event_number: getProperty("event_number", event, 1),
        full_game_time: getProperty("full_game_time", event, 0),
        game_clip_id: getProperty("game_clip_id", event, 0),
        game_id: getProperty("game_id", event, 0),
        team_id: getProperty("team_id", event, 0),
        start_time: getProperty("utc_time", event, 0),
        base_path: videoPlayerBasePath(game),
        duration: getProperty("duration", event, 30),
        half: getProperty("half", event, 1),
        time: getProperty("time", event, 0),
        title: getProperty("title", event, "--"),
        type: getProperty("meta.type", event, "touch"),
        start_third: getProperty("meta.thirds.0", event, "defensive"),
        end_third: getProperty("meta.thirds.1", event, "defensive"),
        dynamic_audio: getProperty("dynamic.audio", event, ""),
        dynamic_duration: getProperty("dynamic.duration", event, 30),
        dynamic_hls: getProperty("dynamic.hls", event, ""),
        dynamic_jfile: getProperty("dynamic.jfile", event, ""),
        dynamic_offset: getProperty("dynamic.offset", event, 0),
        dynamic_start: getProperty("dynamic.start", event, 0),
        keywords: getProperty("meta.keywords", event, []),
        thirds: getProperty("meta.thirds", event, []),
        trace_numbers: getProperty("meta.trace_numbers", event, []),
        home_team: getProperty("meta.home_team", event, null),
        away_team: getProperty("meta.away_team", event, null),
        spotlights: getProperty("meta.spotlights", event, null),
        tags: getProperty("tags", event, null),
        sport_type: getProperty("sport_type", event, "soccer"),
        parent_ref_id: null,
        sources,
        radar,
        meta,
    }
}
